import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { endpoints } from "../lib/api";
import { Crown, ExternalLink, Link2, Clock } from "lucide-react";
import { useCurrency } from "../lib/currency";

function timeLeft(ts) {
  if (!ts) return null;
  const ms = Number(ts) - Date.now();
  if (ms <= 0) return "Ending soon";
  const h = Math.floor(ms / 3.6e6);
  if (h < 1) return `${Math.floor(ms / 60000)}m left`;
  if (h < 48) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}

const PRICE_FILTERS = [
  { id: "all",  label: "Any price",   match: () => true },
  { id: "u10",  label: "Under $10",   match: (p) => p < 10 },
  { id: "10_30",label: "$10 – $30",   match: (p) => p >= 10 && p < 30 },
  { id: "30p",  label: "$30+",        match: (p) => p >= 30 },
];

const LENGTH_FILTERS = [
  { id: "all",  label: "Any length",  match: () => true },
  { id: "3",    label: "3 letters",   match: (l) => l === 3 },
  { id: "4",    label: "4 letters",   match: (l) => l === 4 },
  { id: "5",    label: "5 letters",   match: (l) => l === 5 },
  { id: "6p",   label: "6+ letters",  match: (l) => l >= 6 },
];

export default function PremiumDomains() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [activePrice, setActivePrice] = useState("all");
  const [activeLen, setActiveLen] = useState("all");
  const { formatPrice } = useCurrency();

  useEffect(() => {
    endpoints.premiumDomains().then((r) => {
      setItems(r.data.items || []);
      setCategories(["All", ...(r.data.categories || [])]);
    });
  }, []);

  const priceFn = PRICE_FILTERS.find((f) => f.id === activePrice).match;
  const lenFn = LENGTH_FILTERS.find((f) => f.id === activeLen).match;

  const filtered = useMemo(() => {
    return items.filter(
      (i) =>
        (activeCat === "All" || i.category === activeCat) &&
        priceFn(i.price) &&
        lenFn(i.length ?? (i.name?.split(".")[0]?.length || 0))
    );
  }, [items, activeCat, priceFn, lenFn]);

  return (
    <Layout>
      <Seo
        title="Premium domain names for sale — brandable inventory at closeout prices"
        description="Live premium domain inventory pulled hourly from the aftermarket. Real current prices, renewal cost, and brand appraisals — filter by category, price and length."
        keywords="premium domain names, brandable domains for sale, expired domains, aftermarket domains"
        path="/premium-domains"
      />
      <section data-testid="premium-page">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Premium domains</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">
          Brandable names <span className="text-indigo-600">at closeout prices.</span>
        </h1>
        <p className="mt-3 max-w-2xl text-zinc-600">
          Live aftermarket inventory, filtered for brandability and ranked by independent brand appraisal. Each listing is a real, sellable domain with live pricing — click to buy instantly.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700" data-testid="live-source-badge">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
          Live inventory · refreshed hourly
        </div>

        {/* Filters */}
        <div className="mt-8 space-y-3" data-testid="filter-rows">
          <FilterRow
            label="Category"
            testid="cat"
            options={categories.map((c) => ({ id: c, label: c }))}
            active={activeCat}
            onChange={setActiveCat}
          />
          <FilterRow
            label="Price"
            testid="price"
            options={PRICE_FILTERS}
            active={activePrice}
            onChange={setActivePrice}
          />
          <FilterRow
            label="Length"
            testid="len"
            options={LENGTH_FILTERS}
            active={activeLen}
            onChange={setActiveLen}
          />
        </div>

        <div className="mt-4 text-xs text-zinc-500" data-testid="result-count">
          Showing <span className="font-semibold text-zinc-700">{filtered.length}</span> of {items.length} listings
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((d) => {
            const remaining = timeLeft(d.end_ts);
            return (
              <div
                key={d.name}
                data-testid={`premium-${d.name}`}
                className="group flex flex-col rounded-2xl border border-zinc-100 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md soft-shadow"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                    <Crown className="h-3 w-3" /> Premium
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{d.category}</span>
                </div>
                <div className="font-mono text-2xl font-semibold text-zinc-900 break-all">{d.name}</div>

                {d.appraisal != null && (
                  <div className="mt-2 text-xs text-zinc-500">
                    Brand appraisal{" "}
                    <span className="font-semibold text-zinc-700 tabular-nums">{formatPrice(d.appraisal)}</span>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-3 text-[11px] text-zinc-500">
                  {d.inbound_links > 0 && (
                    <span className="inline-flex items-center gap-1" title="Inbound backlinks pointing at this domain">
                      <Link2 className="h-3 w-3" /> {d.inbound_links} links
                    </span>
                  )}
                  {remaining && (
                    <span className="inline-flex items-center gap-1" title="Closeout ends in">
                      <Clock className="h-3 w-3" /> {remaining}
                    </span>
                  )}
                </div>

                <div className="mt-5 flex items-end justify-between border-t border-zinc-100 pt-4">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Buy now</div>
                    <div className="font-display text-2xl font-semibold text-zinc-900 tabular-nums">
                      {formatPrice(d.price ?? d.estimated_value)}
                    </div>
                    {d.renewal > 0 && (
                      <div className="mt-0.5 text-[11px] text-zinc-500 tabular-nums">
                        Renews {formatPrice(d.renewal)}/yr
                      </div>
                    )}
                  </div>
                  <a
                    href={d.purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-500"
                    data-testid={`buy-${d.name}`}
                  >
                    Buy now <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/40 p-12 text-center text-sm text-zinc-500" data-testid="empty-state">
            No domains match these filters right now. Try widening your selection or check back in an hour.
          </div>
        )}

        <p className="mt-10 text-xs text-zinc-400">
          Prices and availability are pulled live from the aftermarket and may change every few minutes. Closeout prices typically drop hourly until the listing sells.
        </p>
      </section>
    </Layout>
  );
}

function FilterRow({ label, testid, options, active, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 flex-shrink-0 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            data-testid={`${testid}-${o.id}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
              active === o.id
                ? "bg-zinc-900 text-white"
                : "border border-zinc-200 bg-white text-zinc-700 hover:border-indigo-200 hover:text-indigo-700"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
